import {
    commentsActions,
    CommentType,
    CreateMessageBody,
    GetMessageListActionParams,
    GetMessageParams,
    UploadFileType,
    User,
} from '@Techedge/digitalportal-shared'
import { EmojiPicker } from '@Techedge/smart-shared'
import { connect } from 'react-redux'
import { LuxCommentList, LuxAddComment, LuxIconSpinner } from '@lux/wardrobe-lib'
import { useEffect, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import './MessageCommentContainer.scss'
import { FiltersService } from '@/app/services/FiltersService'

export const ROOT_PAGE_NUM = 'root'

export interface MessageCommentContainerProps {
    messageId: string
    projectId: string
    storeIdList: string[]
    topicMap: Record<string, any>
    loadingMore: boolean
    user: User
    createMessage: (
        body: CreateMessageBody,
        topicId: string,
        nodeIds: string[],
        messageId?: string
    ) => void
    getMessageList: (args: GetMessageListActionParams, isDetail?: boolean) => void
    resetTopic: () => void
    editMessage: (body: CreateMessageBody, id: string, nodeIds: string[], parentId: string) => void
    uploadFile: (
        file,
        type: UploadFileType,
        parentId: string,
        topicId: string,
        nodeIds: string[]
    ) => void
    removeFile: (fileId: string, parentId: string, topicId: string, nodeIds: string[]) => void
    deleteMessage: (
        id: string,
        body: GetMessageParams,
        nodeIds: string[],
        parentId: string,
        messageId?: string
    ) => void
    reactionMessage: (
        body: Partial<CreateMessageBody>,
        id: string,
        topicId: string,
        nodeIds: string[],
        parentId: string,
        reaction: string
    ) => void
    loadMore: (
        messageId: string,
        partialBody: GetMessageListActionParams,
        topicId: string,
        nodeIds: string[],
        isDetail: boolean
    ) => void
    initTopic: (topicId: string) => void
    setType: (type: CommentType) => void
}

const MessageCommentContainer = ({
    messageId,
    projectId,
    storeIdList,
    topicMap,
    // loadingMore,
    user,
    createMessage,
    getMessageList,
    resetTopic,
    editMessage,
    uploadFile,
    removeFile,
    deleteMessage,
    reactionMessage,
    loadMore,
    initTopic,
    setType,
}: MessageCommentContainerProps) => {
    const componentClass = 'modals-new-activity-execution-message-comment-container'

    const intl = useIntl()

    const [userStoreList, setUserStoreList] = useState<string[]>(storeIdList)

    const editLabel = useMemo(
        () =>
            intl.formatMessage({
                id: 'forum.edit',
                defaultMessage: 'Edit',
            }),
        [intl]
    )
    const deleteLabel = useMemo(
        () =>
            intl.formatMessage({
                id: 'forum.delete',
                defaultMessage: 'Delete',
            }),
        [intl]
    )
    // const noCommentsLabel = useMemo(
    //     () =>
    //         intl.formatMessage({
    //             id: 'forum.no.comments',
    //             defaultMessage: 'No comments for this topic',
    //         }),
    //     [intl]
    // )
    const loadMoreLabel = useMemo(
        () =>
            intl.formatMessage({
                id: 'forum.load.more',
                defaultMessage: 'Load more',
            }),
        [intl]
    )
    const loadMoreReplyLabel = useMemo(
        () =>
            intl.formatMessage({
                id: 'forum.load.more.reply',
                defaultMessage: 'Load more reply',
            }),
        [intl]
    )
    const replyLabel = useMemo(
        () =>
            intl.formatMessage({
                id: 'forum.reply',
                defaultMessage: 'Reply',
            }),
        [intl]
    )
    const addAttachmentLabel = useMemo(
        () =>
            intl.formatMessage({
                id: 'forum.add.attachment',
                defaultMessage: 'Add attachment',
            }),
        [intl]
    )
    const animatedGifLabel = useMemo(
        () =>
            intl.formatMessage({
                id: 'forum.animated.gif',
                defaultMessage: 'Animated GIF',
            }),
        [intl]
    )
    const emojiLabel = useMemo(
        () =>
            intl.formatMessage({
                id: 'forum.emoji',
                defaultMessage: 'Emoji',
            }),
        [intl]
    )
    const typeHereLabel = useMemo(
        () =>
            intl.formatMessage({
                id: 'forum.type.here',
                defaultMessage: 'Type here...',
            }),
        [intl]
    )

    const onDeleteMessage = (e) => {
        deleteMessage(
            e.id,
            { projectId, parentId: e.parentCommentId || messageId },
            e.nodeIds,
            e.parentCommentId,
            messageId
        )
    }
    const onEditMessage = (e) => {
        editMessage(
            {
                text: e.editedComment,
                isGif: false,
                reference: {
                    projectId,
                    storeIdList: userStoreList,
                    parentId: messageId,
                },
            },
            e.id,
            e.nodeIds,
            e.parentCommentId || messageId
        )
    }
    const onUploadFile = (e) => {
        uploadFile(e.file || e, UploadFileType.FILE, e.id, messageId, e.nodeIds)
    }
    const onRemoveFile = (e) => {
        removeFile(e.file?.id || e.id, !!e.file?.id ? e.id : null, messageId, e.nodeIds)
    }

    const onUpdateTotalReactions = (e) => {
        reactionMessage(
            {
                reference: {
                    projectId,
                    storeIdList: userStoreList,
                    parentId: e.parentCommentId || messageId,
                    messageId,
                },
            },
            e.id,
            messageId,
            e.nodeIds,
            e.parentCommentId || messageId,
            e.type
        )
    }

    const onGif = (e) => {
        onSubmit({
            value: e.file?.images?.original.url || e.images?.original.url,
            isGif: true,
            id: e.file && e.id,
            nodeIds: e.nodeIds,
        })
    }
    const onSubmit = (e) => {
        createMessage(
            {
                text: e.value,
                isGif: e.isGif || false,
                reference: {
                    projectId,
                    storeIdList: userStoreList,
                    parentId: e.id || messageId,
                },
            },
            messageId,
            e.nodeIds || [],
            messageId
        )
    }

    const onLoadMore = (commentId?: string, nodeIds?: string[]) => {
        loadMore(
            commentId,
            {
                projectId,
                storeIdList: userStoreList,
                parentId: nodeIds?.length > 1 ? nodeIds[nodeIds.length - 2] : messageId,
                messageId,
            },
            messageId,
            nodeIds,
            true
        )
    }

    useEffect(() => {
        initTopic(messageId)
        setType(CommentType.PROJECT)

        return () => {
            setType(null)
            resetTopic()
        }
    }, [])

    useEffect(() => {
        if (!!topicMap[messageId]) {
            getMessageList(
                {
                    projectId,
                    messageId,
                    isRootLoading: topicMap[messageId]?.pageNumMap[ROOT_PAGE_NUM] === 1,
                },
                true
            )
        }
    }, [topicMap[messageId]?.pageNumMap[ROOT_PAGE_NUM]])

    useEffect(() => {
        FiltersService.GetStoreList({ mode: 'simple' }).then((res) => {
            const filteredArray = res.data?.stores?.filter((value) => storeIdList.includes(value))
            setUserStoreList(filteredArray)
        })
    }, [storeIdList])

    return (
        <div style={{ marginTop: 16 }} className={componentClass}>
            {topicMap[messageId]?.loading && <LuxIconSpinner />}
            {!topicMap[messageId]?.loading && (
                <div className={`${componentClass}__comments-list-container`}>
                    {topicMap[messageId]?.messageList.length > 0 && (
                        <LuxCommentList
                            creatorInfo={{
                                userName: user?.given_name,
                                userLastname: user?.family_name,
                                userNickname: user?.nickname,
                                avatarImage: user?.profilePicURL,
                            }}
                            commentsList={topicMap[messageId]?.messageList}
                            enableLoadMore={
                                topicMap[messageId]?.messageList.length <
                                topicMap[messageId]?.totalCount
                            }
                            emojiPicker={EmojiPicker}
                            isLoadMoreLoading={topicMap[messageId]?.loadingMore}
                            replyLabel={replyLabel}
                            bgWhite
                            loadMoreLabel={loadMoreLabel}
                            loadMoreReplyLabel={loadMoreReplyLabel}
                            editLabel={editLabel}
                            deleteLabel={deleteLabel}
                            addPlaceholder={typeHereLabel}
                            addLinkLabel={addAttachmentLabel}
                            addGifLabel={animatedGifLabel}
                            addEmojiLabel={emojiLabel}
                            giphyKey={process.env.GIPHY_KEY}
                            onEdit={onEditMessage}
                            onDelete={onDeleteMessage}
                            onRemoveFile={onRemoveFile}
                            onUpload={onUploadFile}
                            onSubmit={onSubmit}
                            onGif={onGif}
                            onLoadMore={onLoadMore}
                            updateTotalReactions={onUpdateTotalReactions}
                            appendTo="self"
                        />
                    )}
                    {/* {!topicMap[messageId]?.messageList.length && (
                        <div className={`${componentClass}__comments-list-empty text`}>
                            {noCommentsLabel}
                        </div>
                    )} */}
                </div>
            )}
            <div className={`${componentClass}__add-comment-container`}>
                <LuxAddComment
                    userName={user.given_name ? user.given_name : ''}
                    userLastname={user.family_name ? user.family_name : ''}
                    userNickname={user.nickname}
                    avatarImage={user?.profilePicURL}
                    emojiPicker={EmojiPicker}
                    placeholder={typeHereLabel}
                    addLinkLabel={addAttachmentLabel}
                    addGifLabel={animatedGifLabel}
                    addEmojiLabel={emojiLabel}
                    attachmentList={topicMap[messageId]?.attachmentMap[ROOT_PAGE_NUM]}
                    isLoading={topicMap[messageId]?.loadingMessage}
                    giphyKey={process.env.GIPHY_KEY}
                    onRemoveFile={onRemoveFile}
                    onUpload={onUploadFile}
                    onSubmit={onSubmit}
                    onGif={onGif}
                />
            </div>
        </div>
    )
}

const mapStateToProps = (state: any) => ({
    topicMap: state.commentsReducer.topicMap,
    loadingMore: state.commentsReducer.loadingMore,
    user: state.userReducer.user,
})

const mapDispatchToProps = (dispatch: any) => ({
    createMessage: (
        body: CreateMessageBody,
        topicId: string,
        nodeIds: string[],
        messageId?: string
    ) => dispatch(commentsActions.createMessage(body, topicId, nodeIds, messageId)),
    getMessageList: (args: GetMessageListActionParams, isDetail?: boolean) =>
        dispatch(commentsActions.getMessageList(args, isDetail)),
    resetTopic: (topicId: string) => {
        dispatch(commentsActions.setTotalCount(0, topicId))
        dispatch(commentsActions.deleteTopic(topicId))
    },
    editMessage: (body: CreateMessageBody, id: string, nodeIds: string[], parentId: string) =>
        dispatch(commentsActions.editMessage(body, id, nodeIds, parentId)),
    uploadFile: (
        file,
        type: UploadFileType,
        parentId: string,
        topicId: string,
        nodeIds: string[]
    ) => dispatch(commentsActions.uploadFile(file, type, parentId, topicId, nodeIds)),
    removeFile: (fileId: string, parentId: string, topicId: string, nodeIds: string[]) =>
        dispatch(commentsActions.removeFile(fileId, parentId, topicId, nodeIds)),
    deleteMessage: (
        id: string,
        body: GetMessageParams,
        nodeIds: string[],
        parentId: string,
        messageId?: string
    ) => dispatch(commentsActions.deleteMessage(id, body, nodeIds, parentId, messageId)),
    reactionMessage: (
        body: CreateMessageBody,
        id: string,
        topicId: string,
        nodeIds: string[],
        parentId: string,
        reaction: string
    ) => dispatch(commentsActions.reactionMessage(body, id, topicId, nodeIds, parentId, reaction)),
    loadMore: (
        messageId: string,
        partialBody: GetMessageListActionParams,
        topicId: string,
        nodeIds: string[],
        isDetail: boolean
    ) => dispatch(commentsActions.loadMore(messageId, partialBody, topicId, nodeIds, isDetail)),
    initTopic: (topicId: string) => dispatch(commentsActions.initTopic(topicId)),
    setType: (type: CommentType) => dispatch(commentsActions.setType(type)),
})

export default connect(mapStateToProps, mapDispatchToProps)(MessageCommentContainer)
